/**
 * XD-62456: the Client Portal guest internet user.
 *
 * The backend identifies the guest purely by this user id (InetUserService.GUEST_INET_USER_ID), compared
 * trimmed and case-insensitively, and there is no guest flag on the profile. The portal used to compare the
 * raw username with `=== 'guestuser9999'` in a handful of places, so a differently-cased login slipped past
 * every check. Compare through {@link isGuestUser} rather than against the constant directly.
 *
 * xd-client-portal#187 (XD-59419) declares the same constant for the order-entry card rule; whichever lands
 * second should import this one rather than keep two copies.
 */
export const GUEST_USERNAME = 'guestuser9999';

export interface GuestUserCandidate {
	/** A string for inet users; a number for workers (AuthenticationService.loginWorker). */
	username?: string | number;
}

/**
 * A guest is always an inet user with a string username. A worker's username is stored as a number
 * (AuthenticationService.loginWorker parses it), so anything that is not a string is never the guest.
 */
export function isGuestUser(user: GuestUserCandidate | null | undefined): boolean {
	const name = user?.username;
	return typeof name === 'string' && name.trim().toLowerCase() === GUEST_USERNAME;
}

/**
 * Shown when the backend refuses a guest request while the guest session is still valid (see
 * Interceptor): the action exists, it is simply not part of guest ordering.
 */
export const GUEST_ACTION_NOT_AVAILABLE_MSG = 'This option is not available when ordering as a guest.';

/** Verbatim backend ClientPortalAuthService.GUEST_ACCOUNT_IS_DISABLED, what a guest login says once it is off. */
export const GUEST_ORDERING_DISABLED_MSG = 'Guest ordering is disabled.';
